import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DocumentScanner as DocumentScannerIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import {
  DataGridPro,
  GridActionsCellItem,
  GridCellModes,
  GridRowModes,
  koKR,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import { v4 as uuidv4 } from 'uuid';
import { DialogTitleClose, AlertDialog } from '../dialog';
import { CustomNoRowsOverlay } from '../datagrid';
import {
  GTestLeadFields,
  areLeadsReady,
  buildClassificationOptions,
  buildLeadJsonData,
  collectLeadKeys,
  joinClassificationParts,
} from '../GTestComparison';
import {
  CaretIntentProvider,
  markClickOffset,
  markSelectAll,
  renderComparisonTextEditCell,
} from '../GTestComparison/comparisonEditCaret';
import { gcoreServerUrl, toFilePreviewUrl } from '../../config';
import * as subMatFolderActions from '../../store/subMatFolder';
import * as gtestComparisonActions from '../../store/gtestComparison';
import * as gstandardActions from '../../store/gstandard';
import { csrfFetch } from '../../store/csrf.js';
import { processGlassLead } from './SubMatFolderClassification';

/** 마스터 criterion에 lead 값 반영 (서버 applyLeadCriterionOnly와 동일 계열) */
const resolveMasterCriterion = (item, leadData) => {
  const rawCriterion = item?.criterion || '';
  if (!item || item.result === '참조값') {
    return { criterion: rawCriterion, noDetailCriterion: false };
  }
  let leadField = item.leadField;
  if (!leadField || !rawCriterion) {
    return { criterion: rawCriterion, noDetailCriterion: false };
  }
  const m = String(leadField).trim().match(/^([a-zA-Z0-9_]+)\s*(:\s*(all|range|rangeAll)?)?/);
  if (!m) {
    return { criterion: rawCriterion, noDetailCriterion: false };
  }
  const key = m[1];
  const leadOption = m[3] || '';
  const leadValue = leadData?.[key] != null ? String(leadData[key]).trim() : '';
  if (!leadValue) {
    return { criterion: rawCriterion, noDetailCriterion: false };
  }
  try {
    if (leadOption.startsWith('range')) {
      const num = Number(leadValue);
      if (Number.isNaN(num)) {
        return { criterion: rawCriterion, noDetailCriterion: false };
      }
      const rangeRe = /(gt|lt|gte|lte|eq)\s*:\s*(-?\d+(?:\.\d+)?)\s*(,\s*(gt|lt|gte|lte|eq)\s*:\s*(-?\d+(?:\.\d+)?))?\s*=([^;]*);?/g;
      let match;
      while ((match = rangeRe.exec(rawCriterion)) !== null) {
        const ok1 = compareLeadOper(match[1], Number(match[2]), num);
        if (!ok1) continue;
        if (match[4]) {
          if (!compareLeadOper(match[4], Number(match[5]), num)) continue;
        }
        const picked = (match[6] || '').trim();
        if (!picked) {
          return {
            criterion: `[평가 기준정보가 존재하지 않습니다.(${key}: ${leadValue})] ${rawCriterion}`,
            noDetailCriterion: true,
          };
        }
        return { criterion: picked, noDetailCriterion: false };
      }
      return {
        criterion: `[평가 기준정보가 존재하지 않습니다.(${key}: ${leadValue})] ${rawCriterion}`,
        noDetailCriterion: true,
      };
    }
    const escaped = leadValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const p = new RegExp(`;?\\s*${escaped}\\s*=([^;]+);?`);
    const found = rawCriterion.match(p);
    if (found) {
      return { criterion: found[1].trim(), noDetailCriterion: false };
    }
    return {
      criterion: `[평가 기준정보가 존재하지 않습니다.(${key}: ${leadValue})] ${rawCriterion}`,
      noDetailCriterion: true,
    };
  } catch (_e) {
    return { criterion: rawCriterion, noDetailCriterion: false };
  }
};

const compareLeadOper = (oper, compValue, leadValue) => {
  switch (oper) {
    case 'gte': return compValue <= leadValue;
    case 'lte': return compValue >= leadValue;
    case 'gt': return compValue < leadValue;
    case 'lt': return compValue > leadValue;
    case 'eq': return compValue === leadValue;
    default: return false;
  }
};

const isQrExtractType = (extractType) => {
  const t = String(extractType || '').toUpperCase();
  return t === 'QR' || t === 'QRCODE' || t.includes('QR');
};

/**
 * 성적서–시험성과대비표
 * 표준 선택 → lead 입력 → 분석(QR 우선, 없으면 표준 마스터 목록)
 * 저장: SubMatFolderFiles(PDF pair) + SubMatTestComparisonItems (레거시 G04docuGTestComparisons 미사용)
 */
const SubMatTestComparisonDialog = ({
  open,
  folder,
  reportFile,
  onClose,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const [fullScreen, setFullScreen] = useState(true);
  const [standards, setStandards] = useState([]);
  const [standard, setStandard] = useState(null);
  const [standardDetail, setStandardDetail] = useState(null);
  const [masterComparisons, setMasterComparisons] = useState([]);
  const [leadKeys, setLeadKeys] = useState([]);
  const [leadData, setLeadData] = useState({});
  const [classificationParts, setClassificationParts] = useState([]);
  const [rows, setRows] = useState([]);
  const [originalRows, setOriginalRows] = useState([]);
  /** originalRows와 짝 — 작업취소 시 extractType도 같이 복원 */
  const [baselineExtractType, setBaselineExtractType] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [extractType, setExtractType] = useState('');
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(true);
  const [rowModesModel, setRowModesModel] = useState({});
  const [cellModesModel, setCellModesModel] = useState({});
  const [alertInfo, setAlertInfo] = useState({});
  const apiRef = useGridApiRef();
  const caretIntentRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await csrfFetch(null, `${gcoreServerUrl}/api/gstandards`);
        const data = await response.json();
        if (!cancelled) setStandards(data?.gstandards || []);
      } catch (_e) {
        if (!cancelled) setStandards([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setStandard(null);
      setStandardDetail(null);
      setMasterComparisons([]);
      setLeadKeys([]);
      setLeadData({});
      setClassificationParts([]);
      setRows([]);
      setOriginalRows([]);
      setBaselineExtractType('');
      setHasChanges(false);
      setExtractType('');
      setBusy(false);
      setLoaded(true);
      setRowModesModel({});
      setCellModesModel({});
      setAlertInfo({});
      setFullScreen(true);
    }
  }, [open]);

  /** 말단 규격이 있으면 대비표 선행값은 그 속성으로 채운다. */
  useEffect(() => {
    if (!open || !standards.length) return;
    const spec = folder?.spec;
    if (!spec?.gstandardId) return;
    const std = standards.find((s) => s.id === spec.gstandardId) || null;
    if (std) setStandard(std);
    const specParts = Array.isArray(spec.classificationParts) ? spec.classificationParts : [];
    const fromGlass = spec.glass?.selectedGcomponentItems
      ? processGlassLead(spec.glass.selectedGcomponentItems, std?.no || '')
      : null;
    const storedThickness = spec.thickness != null ? String(spec.thickness) : '';
    setClassificationParts(specParts);
    setLeadData((ld) => ({
      ...ld,
      thickness: fromGlass?.thickness || storedThickness,
      ftThickness: fromGlass?.ftThickness || '',
      ks2004_thickness: fromGlass?.ks2004_thickness || '',
      classification: joinClassificationParts(specParts),
      classificationType0: specParts[0] || '',
      classificationParts: specParts,
    }));
  }, [open, folder?.spec, standards]);

  /** 저장된 대비표 행·표준·선행값 로드 (재편집) */
  useEffect(() => {
    if (!open || !folder?.id || !reportFile?.id || !standards.length) return undefined;
    let cancelled = false;
    (async () => {
      setBusy(true);
      setLoaded(false);
      try {
        const detail = await dispatch(subMatFolderActions.selectTestComparisonDetail(
          folder.id,
          reportFile.id,
        ));
        if (cancelled) return;
        const savedLead = detail?.leadData && typeof detail.leadData === 'object'
          ? detail.leadData
          : {};
        const savedRows = (detail?.g04docuGTestComparisons || []).map((row) => ({
          ...row,
          id: row.id || uuidv4(),
        }));
        const specId = folder?.spec?.gstandardId;
        if (!specId && detail?.gstandardId) {
          const std = standards.find((s) => s.id === detail.gstandardId) || null;
          if (std) setStandard(std);
        }
        if (!specId) setLeadData(savedLead);
        if (savedRows.length) {
          setRows(savedRows);
          setOriginalRows(JSON.parse(JSON.stringify(savedRows)));
          setBaselineExtractType('SAVED');
          setHasChanges(false);
          setExtractType('SAVED');
        }
      } catch (_err) {
        // 신규 작성: 저장된 대비표 없음
      } finally {
        if (!cancelled) {
          setBusy(false);
          setLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, folder?.id, folder?.spec?.gstandardId, reportFile?.id, standards, dispatch]);

  useEffect(() => {
    if (!open || !standard?.id) {
      setStandardDetail(null);
      setMasterComparisons([]);
      setLeadKeys([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const [detail, comparisons] = await Promise.all([
          gstandardActions.selectDirect(standard.id),
          gtestComparisonActions.selectAllByGstandardIdDirect(standard.id, { evaluation: true }),
        ]);
        if (cancelled) return;
        setStandardDetail(detail || standard);
        setMasterComparisons(comparisons || []);
        setLeadKeys(collectLeadKeys(comparisons));
        const comps = detail?.classificationComponents;
        setClassificationParts((prev) => {
          if (Array.isArray(comps) && comps.length) {
            const fromSpec = folder?.spec?.classificationParts;
            return comps.map((_, i) => (Array.isArray(fromSpec) ? fromSpec[i] : '') || prev[i] || '');
          }
          return [];
        });
      } catch (err) {
        if (!cancelled) {
          setAlertInfo({
            titleAlert: '안내',
            messageAlert: err?.serverResponse?.message || err.message || '표준 정보를 불러오지 못했습니다.',
            open: true,
          });
          setLeadKeys([]);
          setMasterComparisons([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, standard?.id, folder?.spec?.classificationParts]);

  const classificationOptions = useMemo(
    () => buildClassificationOptions(standardDetail),
    [standardDetail],
  );

  const leadsReady = useMemo(
    () => areLeadsReady(leadKeys, leadData),
    [leadKeys, leadData],
  );

  const canAnalyze = Boolean(
    folder?.id && reportFile?.id && standard?.id && leadsReady && !busy,
  );

  const showBuildFromMaster = Boolean(
    standard?.id
    && masterComparisons.length
    && !isQrExtractType(extractType),
  );

  const canBuildFromMaster = showBuildFromMaster && leadsReady && !busy;

  const updateLead = (key, value) => {
    setLeadData((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'classification' && !classificationOptions.length) {
        next.classificationType0 = value || '';
      }
      return next;
    });
  };

  const specLocked = Boolean(folder?.spec?.gstandardId);

  const handleClassificationPartChange = (colIdx, value) => {
    setClassificationParts((prev) => {
      const next = [...prev];
      next[colIdx] = value;
      const joined = joinClassificationParts(next);
      setLeadData((ld) => ({
        ...ld,
        classification: joined || '',
        classificationType0: next[0] || '',
        classificationParts: next,
      }));
      return next;
    });
  };

  const buildJsonData = () => buildLeadJsonData(leadKeys, leadData);

  const applyExtractRows = (list, type) => {
    const newRows = (list || []).map((row) => ({
      ...row,
      id: row.id || uuidv4(),
    }));
    const nextType = type || '';
    setRows(newRows);
    setOriginalRows(JSON.parse(JSON.stringify(newRows)));
    setBaselineExtractType(nextType);
    setHasChanges(false);
    setExtractType(nextType);
    return newRows;
  };

  const handleAnalyze = async () => {
    if (!folder?.id || !reportFile?.id) return;
    if (!standard?.id) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '먼저 표준번호를 선택하세요. 표준을 기준으로 선행 입력란과 평가 기준이 결정됩니다.',
        open: true,
      });
      return;
    }
    if (!leadsReady) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '선행값(두께·등급 등)을 모두 입력한 뒤 성적서를 분석할 수 있습니다.',
        open: true,
      });
      return;
    }
    // 분석: QR 1순위 → 실패·비QR이면 표준 마스터(GTestComparisons)+lead로 목록 구성 (서버 fillTestComparisons)
    setBusy(true);
    setLoaded(false);
    try {
      const result = await dispatch(subMatFolderActions.extractTestComparison(folder.id, {
        gstandardId: standard.id,
        reportFileId: reportFile.id,
        jsonData: buildJsonData(),
      }));
      const list = result?.g04docuGTestComparisons || [];
      applyExtractRows(list, result?.extractType);
      if (!list.length) {
        setAlertInfo({
          titleAlert: '안내',
          messageAlert: '분석 결과가 없습니다. 선행 입력값(두께·등급 등)을 확인한 뒤 다시 분석해 주세요.',
          open: true,
        });
      }
    } catch (err) {
      setRows([]);
      setOriginalRows([]);
      setBaselineExtractType('');
      setExtractType('');
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: err?.serverResponse?.message || err.message || '분석에 실패했습니다.',
        open: true,
      });
    } finally {
      setBusy(false);
      setLoaded(true);
    }
  };

  const handleClickAdd = () => {
    const id = uuidv4();
    setRows((oldRows) => [
      ...oldRows,
      { id, item: '시험항목', unit: '단위', criterion: '기준', value: '', result: '', method: '', isNew: true },
    ]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.View, fieldToFocus: 'item' },
    }));
    setHasChanges(true);
  };

  const handleDeleteClick = (id) => () => {
    const api = apiRef.current;
    const editModel = typeof api?.getEditRowsModel === 'function' ? api.getEditRowsModel() : {};
    Object.keys(editModel || {}).forEach((rowId) => {
      Object.keys(editModel[rowId] || {}).forEach((field) => {
        try {
          api.commitCellChange({ id: rowId, field });
          api.setCellMode(rowId, field, 'view');
        } catch (_e) {
          // 편집 중이 아니면 무시
        }
      });
    });
    setRows((prev) => prev.filter((row) => row.id !== id));
    setHasChanges(true);
  };

  const handleUndo = () => {
    if (originalRows.length > 0) {
      setRows(JSON.parse(JSON.stringify(originalRows)));
      setExtractType(baselineExtractType);
      setHasChanges(false);
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '수정한 내용이 원래의 상태로 되돌려졌습니다.',
        open: true,
      });
    } else {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '되돌릴 원본 데이터가 없습니다.',
        open: true,
      });
    }
  };

  /** 표준 마스터 항목으로 목록 구성 (QR 실패·비QR 시). lead로 기준 해석. 화면만 변경. */
  const applyMasterRows = () => {
    if (!masterComparisons.length) return;
    const json = buildJsonData();
    const newRows = masterComparisons.map((c) => {
      const resolved = resolveMasterCriterion(c, json);
      return {
        id: uuidv4(),
        item: c.item,
        unit: c.unit,
        criterion: resolved.criterion,
        value: '',
        result: c.result === '참조값' ? '참조값' : '',
        method: c.method || '',
        office: null,
        noDetailCriterion: resolved.noDetailCriterion,
      };
    });
    // originalRows·baselineExtractType은 유지 → 「작업취소」로 이전 목록 복원
    setRows(newRows);
    setHasChanges(true);
    setExtractType('MASTER');
  };

  const handleBuildFromMasterClick = () => {
    if (!canBuildFromMaster) {
      if (!leadsReady) {
        setAlertInfo({
          titleAlert: '안내',
          messageAlert: '선행값(두께·등급 등)을 모두 입력한 뒤 「표준항목으로 구성」을 사용할 수 있습니다.',
          open: true,
        });
      }
      return;
    }
    setAlertInfo({
      type: 'confirm',
      titleAlert: '표준항목으로 구성',
      messageAlert: (
        <span>
          선택한 표준의 시험항목으로 목록을 다시 구성합니다.
          <br />
          선행값(두께·등급 등)에 맞는 기준이 적용되며, 현재 목록(QR·편집 내용)은 사라집니다.
          <br />
          <br />
          <strong>지금은 화면만 바뀝니다.</strong>
          {' '}
          「저장 및 PDF 작성」을 눌러야 데이터와 PDF에 최종 반영됩니다.
          <br />
          <br />
          계속하시겠습니까?
        </span>
      ),
      open: true,
      confirmText: '구성',
      cancelText: '취소',
      handleConfirm: () => {
        applyMasterRows();
      },
    });
  };

  const handleSavePdf = async () => {
    if (!folder?.id || !reportFile?.id || !rows.length) return;
    setBusy(true);
    setLoaded(false);
    try {
      await dispatch(subMatFolderActions.createTestComparisonPdf(folder.id, {
        g04docuGTestComparisons: rows,
        productName: folder.name,
        reportFileId: reportFile.id,
        gstandardId: standard?.id || null,
        jsonData: buildJsonData(),
      }));
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: (
          <div>
            <span style={{ color: '#1976d2' }}>시험성과대비표</span>
            <span> 데이터와 PDF가 저장되었습니다.</span>
          </div>
        ),
        open: true,
      });
      setHasChanges(false);
      setOriginalRows(JSON.parse(JSON.stringify(rows)));
      setBaselineExtractType(extractType || 'SAVED');
      onSaved?.(folder.id);
    } catch (err) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: err?.serverResponse?.message || err.message || '저장 및 PDF 작성에 실패했습니다.',
        open: true,
      });
    } finally {
      setBusy(false);
      setLoaded(true);
    }
  };

  const processRowUpdate = useCallback((newRow) => {
    setRows((prev) => prev.map((row) => (row.id === newRow.id ? newRow : row)));
    setHasChanges(true);
    return newRow;
  }, []);

  /** 레거시와 동일: 셀 커밋 시 rows에 바로 반영 (다른 행 삭제 시 수정값 유지) */
  const handleCellEditCommit = useCallback((params) => {
    const { id, field, value } = params;
    setRows((prev) => prev.map((row) => (
      row.id === id ? { ...row, [field]: value } : row
    )));
    setHasChanges(true);
  }, []);

  const handleCellKeyDown = (params, event) => {
    if (
      params.cellMode !== 'edit'
      && params.isEditable
      && event.key === ' '
      && !event.shiftKey
      && !event.ctrlKey
      && !event.metaKey
      && !event.altKey
    ) {
      event.defaultMuiPrevented = true;
      event.preventDefault();
      markSelectAll(caretIntentRef, params.id, params.field);
      apiRef.current.setCellMode(params.id, params.field, 'edit');
      return;
    }
    if (params.cellMode !== 'edit' && params.isEditable && event.key === 'Enter') {
      markSelectAll(caretIntentRef, params.id, params.field);
    }
    // 보기 상태의 글자 키는 편집을 열지 않는다. 입력칸은 엔터나 스페이스로 만든 뒤에만 친다.
    if (
      params.cellMode !== 'edit'
      && params.isEditable
      && !event.ctrlKey
      && !event.metaKey
      && !event.altKey
      && event.key.length === 1
    ) {
      event.defaultMuiPrevented = true;
      return;
    }
    if (event.key !== 'Enter' || params.cellMode !== 'edit') return;
    const updatedRow = { ...params.row, [params.field]: event.target.value };
    processRowUpdate(updatedRow);
    setCellModesModel((prevModel) => ({
      ...prevModel,
      [params.id]: {
        ...prevModel[params.id],
        [params.field]: { mode: GridCellModes.View },
      },
    }));
  };

  const handleCellClick = useCallback((params, event) => {
    if (!params.isEditable || params.cellMode === 'edit') return;
    if (event.target.nodeType === 1 && !event.currentTarget.contains(event.target)) return;
    const focusedCell = apiRef.current.state?.focus?.cell;
    if (!focusedCell || focusedCell.id !== params.id || focusedCell.field !== params.field) return;
    markClickOffset(caretIntentRef, params.id, params.field, event.clientX, event.clientY);
    apiRef.current.setCellMode(params.id, params.field, 'edit');
  }, [apiRef]);

  const handleCellDoubleClick = useCallback((params, event) => {
    if (!params.isEditable || params.cellMode === 'edit') return;
    markClickOffset(caretIntentRef, params.id, params.field, event.clientX, event.clientY);
  }, []);

  const handleRowOrderChange = (params) => {
    const { oldIndex, targetIndex } = params;
    setRows((prev) => {
      const next = [...prev];
      const [moved] = next.splice(oldIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
    setHasChanges(true);
  };

  const columns = useMemo(() => [
    {
      field: 'item',
      headerName: '구분',
      editable: true,
      width: 160,
      renderEditCell: renderComparisonTextEditCell,
    },
    {
      field: 'unit',
      headerName: '단위',
      editable: true,
      width: 100,
      renderEditCell: renderComparisonTextEditCell,
    },
    {
      field: 'criterion',
      headerName: '기준',
      editable: true,
      width: 300,
      renderEditCell: renderComparisonTextEditCell,
      renderCell: (params) => {
        if (params.row.noDetailCriterion) {
          return (
            <Tooltip title="선행값에 맞는 평가 기준정보가 없습니다.">
              <span style={{ color: '#DB4E2E', fontWeight: 'bold' }}>{params.value}</span>
            </Tooltip>
          );
        }
        return params.value;
      },
    },
    {
      field: 'value',
      headerName: '시험결과',
      editable: true,
      width: 120,
      renderEditCell: renderComparisonTextEditCell,
    },
    {
      field: 'result',
      headerName: '판정',
      width: 120,
      editable: true,
      type: 'singleSelect',
      valueOptions: ['합격', '불합격', '참조값'],
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        if (params.value === '합격') {
          return <span style={{ color: '#1976d2', fontWeight: 'bold' }}>합격</span>;
        }
        if (params.value === '불합격') {
          return <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>불합격</span>;
        }
        return params.value;
      },
    },
    {
      field: 'method',
      headerName: '시험방법',
      editable: true,
      width: 120,
      renderEditCell: renderComparisonTextEditCell,
    },
    {
      field: 'office',
      headerName: '시험기관',
      hide: true,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '기능',
      width: 87,
      cellClassName: 'actions',
      getActions: ({ id, row }) => {
        if (row.office) return [];
        return [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ], []);

  // documentPath 고정 시 URL 고정 — lead 입력 리렌더마다 난수 query로 iframe PDF가 재로딩되지 않게
  const reportPreviewUrl = useMemo(
    () => (reportFile?.documentPath ? toFilePreviewUrl(reportFile.documentPath) : ''),
    [reportFile?.documentPath],
  );

  const reportLabel = reportFile?.fileName || reportFile?.id || '';

  return (
    <>
      <Dialog
        fullScreen={fullScreen}
        open={open}
        disableEscapeKeyDown
        onClose={busy ? undefined : onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: fullScreen ? '100vh' : '90vh',
            maxHeight: fullScreen ? '100vh' : '90vh',
          },
        }}
      >
        <DialogTitleClose
          onClose={busy ? undefined : onClose}
          onMinMax={() => setFullScreen((v) => !v)}
          fullScreen={fullScreen}
          sx={{
            backgroundColor: fullScreen ? '#1976d2' : 'transparent',
            color: fullScreen ? 'white' : 'inherit',
          }}
        >
          <Typography variant="h6">
            {reportFile?.comparisonFile || extractType === 'SAVED'
              ? '시험성과대비표 수정'
              : '시험성과대비표 만들기'}
          </Typography>
          <Typography variant="body2" color={fullScreen ? 'white' : 'text.secondary'}>
            {folder?.name || ''}
            {reportLabel ? ` — ${reportLabel}` : ''}
          </Typography>
        </DialogTitleClose>

        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ px: 3, pt: 2, pb: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            <Box sx={{ mb: 1.5 }}>
              <Autocomplete
                sx={{ minWidth: 280, maxWidth: 480 }}
                options={standards}
                value={standard}
                onChange={(_e, v) => {
                  setStandard(v);
                  setLeadData({});
                  setClassificationParts([]);
                  setRows([]);
                  setOriginalRows([]);
                  setBaselineExtractType('');
                  setHasChanges(false);
                  setExtractType('');
                }}
                getOptionLabel={(o) => {
                  if (!o) return '';
                  const no = o.no || o.code || '';
                  return no ? `${no} — ${o.name || ''}` : (o.name || o.id || '');
                }}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                disabled={specLocked}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="표준번호"
                    size="small"
                    required
                  />
                )}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              {specLocked && (
                <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>
                  이 폴더에 저장한 규격으로 두께와 등급 1단을 합성합니다.
                </Typography>
              )}
              <GTestLeadFields
                hasStandard={Boolean(standard?.id)}
                leadKeys={leadKeys}
                leadData={leadData}
                disabled={specLocked}
                onLeadChange={updateLead}
                classificationParts={classificationParts}
                onClassificationPartChange={handleClassificationPartChange}
                classificationOptions={classificationOptions}
                disabled={specLocked || busy}
              />
              <Tooltip
                title={
                  !standard?.id
                    ? '먼저 표준번호를 선택하세요.'
                    : !leadsReady
                      ? '선행값(두께·등급 등)을 모두 입력해야 분석할 수 있습니다.'
                      : '성적서를 분석하여 시험성과대비표 목록을 구성합니다. (QR 우선)'
                }
              >
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DocumentScannerIcon />}
                    onClick={handleAnalyze}
                    disabled={!canAnalyze}
                    sx={{
                      height: 40,
                      py: 0,
                      boxSizing: 'border-box',
                      '& .MuiButton-startIcon': { marginRight: 0.75 },
                      '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 18 },
                    }}
                  >
                    성적서 분석
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {(busy || !loaded) && <LinearProgress />}

          <Card sx={{ m: 2, p: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <PanelGroup direction="horizontal" style={{ flex: 1, minHeight: 480 }}>
              <Panel defaultSize={40} minSize={20} maxSize={80}>
                <Box sx={{ pr: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 'bold', mb: 1 }}>
                    ∙ 성적서
                  </Typography>
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    {reportPreviewUrl ? (
                      <iframe
                        title="성적서 미리보기"
                        src={reportPreviewUrl}
                        width="100%"
                        height="100%"
                        style={{ border: '1px solid #ddd', borderRadius: 4 }}
                      />
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          border: '1px solid #ddd',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          성적서가 없습니다.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Panel>

              <PanelResizeHandle
                style={{
                  width: 8,
                  backgroundColor: '#e0e0e0',
                  cursor: 'col-resize',
                }}
              />

              <Panel defaultSize={60} minSize={20} maxSize={80}>
                <Box sx={{ pl: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Grid container spacing={1} sx={{ mb: 1 }} alignItems="center">
                    <Grid item xs={12} sm={12} md={3} lg={2}>
                      <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                        ∙ 시험성과대비표
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={9}
                      lg={10}
                      display="flex"
                      justifyContent="flex-end"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={1}
                    >
                      <Tooltip title="목록의 마지막에 시험항목 한 행을 추가합니다.">
                        <Button color="primary" startIcon={<AddIcon />} onClick={handleClickAdd} size="small">
                          추가
                        </Button>
                      </Tooltip>
                      <Tooltip title="시험성과대비표 행 데이터와 PDF를 함께 저장합니다.">
                        <Button
                          color="primary"
                          startIcon={<SaveIcon />}
                          onClick={handleSavePdf}
                          size="small"
                          disabled={busy || rows.length === 0 || !reportFile?.id}
                        >
                          저장 및 PDF 작성
                        </Button>
                      </Tooltip>
                      <Tooltip title="수정하신 내용을 취소하고 원래의 상태로 되돌립니다.">
                        <Button
                          color="primary"
                          startIcon={<UndoIcon />}
                          onClick={handleUndo}
                          disabled={!hasChanges}
                          size="small"
                        >
                          작업취소
                        </Button>
                      </Tooltip>
                      {showBuildFromMaster && (
                        <Tooltip
                          title={
                            leadsReady
                              ? '표준 시험항목으로 목록을 구성합니다. 선행값에 맞는 기준이 적용됩니다.'
                              : '선행값(두께·등급 등)을 모두 입력해야 사용할 수 있습니다.'
                          }
                        >
                          <span>
                            <Button
                              color="primary"
                              startIcon={<RefreshIcon />}
                              onClick={handleBuildFromMasterClick}
                              size="small"
                              disabled={!canBuildFromMaster}
                            >
                              표준항목으로 구성
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Grid>
                  </Grid>

                  <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                    <CaretIntentProvider value={caretIntentRef}>
                    <DataGridPro
                      localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
                      getCellClassName={(params) => {
                        if (params.row.office && !['criterion', 'result', 'method'].includes(params.field)) {
                          return 'disabled-row';
                        }
                        return '';
                      }}
                      sx={{
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        height: '100%',
                        '& .disabled-row': {
                          '&:hover': { backgroundColor: '#eeeeee' },
                          opacity: 0.85,
                          cursor: 'not-allowed',
                        },
                      }}
                      slots={{
                        noRowsOverlay: CustomNoRowsOverlay,
                        loadingOverlay: LinearProgress,
                      }}
                      headerHeight={38}
                      rowHeight={34}
                      rows={rows}
                      columns={columns}
                      loading={!loaded}
                      disableColumnMenu
                      rowReordering
                      editMode="cell"
                      isCellEditable={(params) => {
                        if (params.field === 'criterion' || params.field === 'result' || params.field === 'method') {
                          return true;
                        }
                        return !params.row.office;
                      }}
                      apiRef={apiRef}
                      onCellModesModelChange={setCellModesModel}
                      onCellClick={handleCellClick}
                      onCellDoubleClick={handleCellDoubleClick}
                      onCellEditCommit={handleCellEditCommit}
                      onCellKeyDown={handleCellKeyDown}
                      processRowUpdate={processRowUpdate}
                      rowModesModel={rowModesModel}
                      onRowModesModelChange={setRowModesModel}
                      cellModesModel={cellModesModel}
                      onRowOrderChange={handleRowOrderChange}
                    />
                    </CaretIntentProvider>
                  </Box>
                </Box>
              </Panel>
            </PanelGroup>
          </Card>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={busy}>닫기</Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        alertInfo={alertInfo}
        setAlertInfo={setAlertInfo}
      />
    </>
  );
};

export default SubMatTestComparisonDialog;
